// IE 2026 09 05 / ODOO-336379: пул Web Worker'ов с pyodide для python-пересчёта после правки (AttrGrid.pyRecalc_batch).
// Главный поток: копия блока строк (pyWorker_copyRows; предки — pyWorker_copyScope) → worker (структурная копия или JSON) → диф изменённых ячеек →
// применение к живым строкам (pyWorker_applyDiff) со стоп-гардом по старому значению. Флаги: py_worker (воркер вообще),
// py_worker_pool (несколько воркеров для пакета SKU), py_worker_json (JSON вместо structured clone — для замера).
// Размер пула = min(navigator.hardwareConcurrency - 1, 4), не меньше 1; на ≤2 ядрах — один воркер.
// Прогрев ленивый (warm): воркеры стартуют по одному, модуль отчёта грузится во все; при недоступности/ошибке —
// главный поток считает как раньше (см. AttrGrid.process_dataItem_py).
import { toRaw } from 'vue'

const PYODIDE_CDN = 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/pyodide.js'
const PYODIDE_INDEX = 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/'
const REQUEST_TIMEOUT_MS = 180000

export function pyWorker_poolSize() {
    const n = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 2
    return Math.max(1, Math.min(n - 1, 4))
}

export function pyWorker_supported() {
    return typeof Worker !== 'undefined' && typeof URL !== 'undefined'
}

// плоская копия строк блока (дерево по childPath): без функций/символов/циклов, сырые объекты Vue (toRaw);
// каждой строке гарантируется _fe.key (nextKey), byKey заполняется ссылками на ЖИВЫЕ строки для применения дифа
export function pyWorker_copyRows(list, childPath, nextKey, byKey) {
    const seen = new WeakSet()
    const copyVal = (v, depth) => {
        if (v === null || typeof v !== 'object') return (typeof v === 'function' || typeof v === 'symbol') ? undefined : v
        const r = toRaw(v)
        if (r instanceof Date) return new Date(r.getTime())
        if (seen.has(r) || depth > 40) return undefined
        seen.add(r)
        let out
        if (Array.isArray(r)) {
            out = new Array(r.length)
            for (let i = 0; i < r.length; i++) out[i] = copyVal(r[i], depth + 1)
        } else {
            out = {}
            for (const k of Object.keys(r)) {
                if (k === '$findMarker') continue
                const c = copyVal(r[k], depth + 1)
                if (c !== undefined || r[k] === undefined) out[k] = c
            }
        }
        seen.delete(r)
        return out
    }
    const copyRows = (arr, depth) => {
        const raw = toRaw(arr)
        const out = new Array(raw.length)
        for (let i = 0; i < raw.length; i++) {
            const it = raw[i]
            out[i] = (it && typeof it === 'object' && !Array.isArray(it) && !(it instanceof Date)) ? copyRow(it, depth) : copyVal(it, depth)
        }
        return out
    }
    const copyRow = (it, depth) => {
        const r = toRaw(it)
        if (!r._fe) it._fe = { key: nextKey() }
        else if (!r._fe.key) it._fe.key = nextKey()
        if (byKey) byKey.set(r._fe.key, it)
        const out = {}
        seen.add(r)
        for (const k of Object.keys(r)) {
            if (k === '$findMarker') continue
            if (childPath && k === childPath && Array.isArray(r[k]) && depth < 40) { out[k] = copyRows(r[k], depth + 1); continue }
            const c = copyVal(r[k], depth + 1)
            if (c !== undefined || r[k] === undefined) out[k] = c
        }
        seen.delete(r)
        return out
    }
    return copyRows(Array.isArray(list) ? list : [list], 0)
}

// IE 2026 09 05 / ODOO-336379: копия блока уровня 0 для пересуммирования предков в воркере — минимально достаточная:
// записи цепочки (chainSet) и SKU копируются целиком (со всеми строками-мерами детей), прочие бренды — только своими
// строками-агрегатами (их SKU в сумму уровня выше не входят: у SKU-строки нет меры/года). byKey — живые строки по ключу
export function pyWorker_copyScope(root, chainSet, childPath, opts) {
    const { isSku, isBrand, nextKey, byKey } = opts
    const chainRaw = new Set([...chainSet].map(x => toRaw(x)))   // записи цепочки могут быть реактивными прокси — сравниваем сырые объекты
    const ensureKey = (it) => {
        const r = toRaw(it)
        if (!r._fe) it._fe = { key: nextKey() }
        else if (!r._fe.key) it._fe.key = nextKey()
        return r._fe.key
    }
    const copyRow = (it, mode, depth) => {
        // mode: 'full' — со всем поддеревом; 'groups' — только строки-агрегаты среди детей
        const r = toRaw(it)
        const key = ensureKey(it)
        byKey.set(key, it)
        const out = {}
        for (const k of Object.keys(r)) {
            if (k === '$findMarker') continue
            if (childPath && k === childPath && Array.isArray(r[k]) && depth < 40) {
                const kids = toRaw(r[k])
                const arr = []
                for (const kid of kids) {
                    if (!kid || typeof kid !== 'object') { arr.push(pyWorker_plain(kid)); continue }
                    if (mode === 'full') { arr.push(copyRow(kid, chainRaw.has(toRaw(kid)) || isSku(kid) ? 'full' : (isBrand(kid) ? 'groups' : 'full'), depth + 1)); continue }
                    if (isSku(kid) || isBrand(kid)) continue   // groups: дети-агрегаты только
                    arr.push(copyRow(kid, 'groups', depth + 1))
                }
                out[k] = arr
                continue
            }
            const c = pyWorker_plain(r[k])
            if (c !== undefined || r[k] === undefined) out[k] = c
        }
        return out
    }
    return [copyRow(root, 'full', 0)]
}

// глубокая плоская копия произвольного значения (без функций/символов/циклов, сырые объекты Vue)
export function pyWorker_plain(v) {
    const seen = new WeakSet()
    const copyVal = (x, depth) => {
        if (x === null || typeof x !== 'object') return (typeof x === 'function' || typeof x === 'symbol') ? undefined : x
        const r = toRaw(x)
        if (r instanceof Date) return new Date(r.getTime())
        if (seen.has(r) || depth > 40) return undefined
        seen.add(r)
        let out
        if (Array.isArray(r)) {
            out = new Array(r.length)
            for (let i = 0; i < r.length; i++) out[i] = copyVal(r[i], depth + 1)
        } else {
            out = {}
            for (const k of Object.keys(r)) {
                const c = copyVal(r[k], depth + 1)
                if (c !== undefined || r[k] === undefined) out[k] = c
            }
        }
        seen.delete(r)
        return out
    }
    return copyVal(v, 0)
}

export function pyWorker_sameVal(a, b) {
    if (a === b) return true
    if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b)
    if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
    if (a === null || b === null || a === undefined || b === undefined) return false
    if (typeof a === 'object' && typeof b === 'object') {
        try { return JSON.stringify(toRaw(a)) === JSON.stringify(toRaw(b)) } catch (e) { return false }
    }
    return false
}

// применить диф воркера к живым строкам; стоп-гард: если текущее значение ячейки не равно значению на момент отправки
// (пользователь правил во время расчёта) — пропуск (строка и так стоит в очереди на пересчёт)
export function pyWorker_applyDiff(diff, byKey) {
    const st = { applied: 0, stale: 0, missing: 0, root: 0 }
    for (const e of diff || []) {
        if (e.k === '__root__') { st.root++; continue }
        const item = byKey.get(e.k)
        if (!item) { st.missing++; continue }
        let obj = item
        const p = e.p
        for (let i = 0; i < p.length - 1; i++) {
            let nx = obj[p[i]]
            if (nx === null || typeof nx !== 'object') {
                if (e.d) { obj = null; break }
                nx = obj[p[i]] = {}
            }
            obj = nx
        }
        if (!obj) continue
        const leaf = p[p.length - 1]
        if (!pyWorker_sameVal(obj[leaf], e.o)) { st.stale++; continue }
        if (e.d) delete obj[leaf]
        else obj[leaf] = e.v
        st.applied++
    }
    return st
}

export class PyWorkerPool {
    constructor(opts = {}) {
        this.size = Math.max(1, opts.size || pyWorker_poolSize())
        this.utilsText = opts.utilsText || ''
        this.utilsUrl = opts.utilsUrl || ''
        this.log = opts.log || (() => {})
        this.onErr = opts.onErr || (() => {})
        this.workers = []      // { i, w, ready(Promise), isReady, dead, busy, loaded: Map(modName→sig), loading: Map(modName→Promise), pending: Map(id→{resolve,reject,t}) }
        this._id = 0
        this._warming = null
        this.stats = { spawned: 0, died: 0, requests: 0, errors: 0 }
    }
    _spawn() {
        const i = this.workers.length
        let w
        try {
            w = new Worker(new URL('./py.worker.js', import.meta.url))
        } catch (e) {
            this.log(`[gridPerf] py worker: cannot start (${String(e)})`)
            return null
        }
        const rec = { i, w, isReady: false, dead: false, busy: 0, loaded: new Map(), loading: new Map(), pending: new Map(), ready: null }
        w.onmessage = (e) => this._onMessage(rec, e.data || {})
        w.onerror = (e) => {
            this.log(`[gridPerf] py worker #${i} error: ${e && e.message || e}`)
            this._kill(rec)
        }
        rec.ready = this._request(rec, { type: 'init', cdn: PYODIDE_CDN, indexURL: PYODIDE_INDEX, utilsText: this.utilsText, utilsUrl: this.utilsUrl })
            .then((r) => { rec.isReady = true; rec.initMs = r.ms; this.log(`[gridPerf] py worker #${i} ready in ${r.ms} ms`); return rec })
            .catch((err) => { this.log(`[gridPerf] py worker #${i} init failed: ${String(err)}`); this._kill(rec); throw err })
        this.workers.push(rec)
        this.stats.spawned++
        return rec
    }
    _kill(rec) {
        if (rec.dead) return
        rec.dead = true
        rec.isReady = false
        this.stats.died++
        for (const [, p] of rec.pending) { clearTimeout(p.t); p.reject(new Error('py worker died')) }
        rec.pending.clear()
        try { rec.w.terminate() } catch (e) { /* уже завершён */ }
        const k = this.workers.indexOf(rec)
        if (k >= 0) this.workers.splice(k, 1)
    }
    _onMessage(rec, m) {
        if (m.type === 'log') { console.log(m.text); return }
        if (m.type === 'err') { console.error(m.text); this.onErr(m.text); return }
        const p = m.id != null ? rec.pending.get(m.id) : null
        if (!p) return
        rec.pending.delete(m.id)
        clearTimeout(p.t)
        if (m.type === 'error') { this.stats.errors++; p.reject(new Error(m.error || 'py worker error')) }
        else p.resolve(m)
    }
    _request(rec, msg, transfer) {
        const id = ++this._id
        return new Promise((resolve, reject) => {
            const t = setTimeout(() => {
                if (!rec.pending.has(id)) return
                rec.pending.delete(id)
                this.stats.errors++
                reject(new Error(`py worker #${rec.i} timeout (${msg.type})`))
                this._kill(rec)
            }, REQUEST_TIMEOUT_MS)
            rec.pending.set(id, { resolve, reject, t })
            try { rec.w.postMessage({ ...msg, id }, transfer || []) }
            catch (e) { rec.pending.delete(id); clearTimeout(t); reject(e) }
        })
    }
    // модуль отчёта в воркере (кэш по sig)
    _load(rec, spec) {
        if (rec.loaded.get(spec.modName) === spec.sig) return Promise.resolve(rec)
        const cur = rec.loading.get(spec.modName)
        if (cur && cur.sig === spec.sig) return cur.p
        const p = rec.ready.then(() => this._request(rec, { type: 'load', modName: spec.modName, sig: spec.sig, modules: spec.modules, skuFn: spec.skuFn, helperSrc: spec.helperSrc || '', fnNames: spec.fnNames }))
            .then((r) => {
                rec.loaded.set(spec.modName, spec.sig)
                rec.fns = r.fns
                this.log(`[gridPerf] py worker #${rec.i}: module ${spec.modName} loaded in ${r.ms} ms${r.cached ? ' (cached)' : ''}`)
                return rec
            })
            .finally(() => { if (rec.loading.get(spec.modName)?.p === p) rec.loading.delete(spec.modName) })
        rec.loading.set(spec.modName, { sig: spec.sig, p })
        return p
    }
    // ленивый прогрев: count воркеров по одному (следующий — после готовности предыдущего), модуль во всех
    warm(spec, count = 1) {
        count = Math.max(1, Math.min(count, this.size))
        const step = async () => {
            for (let i = 0; i < count; i++) {
                let rec = this.workers[i]
                if (!rec) rec = this._spawn()
                if (!rec) return
                try { await this._load(rec, spec) } catch (e) { /* воркер упал — главный поток продолжает считать сам */ }
            }
        }
        const p = (this._warming || Promise.resolve()).then(step).catch(() => {})
        this._warming = p
        return p
    }
    // воркеры, готовые считать этот модуль прямо сейчас (без ожидания)
    readyFor(spec, limit = 0) {
        const list = this.workers.filter(r => r.isReady && !r.dead && r.loaded.get(spec.modName) === spec.sig)
        return limit > 0 ? list.slice(0, limit) : list   // py_worker_pool OFF → только первый воркер, даже если прогреты все
    }
    // пересчёт в наименее занятом готовом воркере
    recalc(spec, job, json, limit = 0) {
        const ready = this.readyFor(spec, limit)
        if (!ready.length) return Promise.reject(new Error('no ready py worker'))
        let rec = ready[0]
        for (const r of ready) if (r.busy < rec.busy) rec = r
        rec.busy++
        this.stats.requests++
        const msg = { type: 'recalc', modName: spec.modName, sig: spec.sig, afterEditName: spec.afterEditName, ...job }
        if (json) { msg.json = json; delete msg.roots }
        const t0 = performance.now()
        return this._request(rec, msg).then((r) => { r.worker = rec.i; r.wall = Math.round(performance.now() - t0); return r }).finally(() => { rec.busy-- })
    }
    // IE 2026 09 05 / ODOO-336379: пересуммирование предков (фаза 2) в наименее занятом готовом воркере
    reaggr(spec, job, limit = 0) {
        const ready = this.readyFor(spec, limit)
        if (!ready.length) return Promise.reject(new Error('no ready py worker'))
        let rec = ready[0]
        for (const r of ready) if (r.busy < rec.busy) rec = r
        rec.busy++
        this.stats.requests++
        const msg = { type: 'reaggr', modName: spec.modName, sig: spec.sig, ...job }
        const t0 = performance.now()
        return this._request(rec, msg).then((r) => { r.worker = rec.i; r.wall = Math.round(performance.now() - t0); return r }).finally(() => { rec.busy-- })
    }
    info() {
        return { size: this.size, workers: this.workers.map(r => ({ i: r.i, ready: r.isReady, busy: r.busy, initMs: r.initMs, loaded: [...r.loaded.keys()] })), ...this.stats }
    }
    dispose() {
        for (const rec of [...this.workers]) this._kill(rec)
    }
}
